/**
 * Per-tab active-account RESOLUTION: turns "the id this tab is pinned to"
 * (`lib/tab-account.ts`) plus the shared registry into the account, sync user
 * and identity view the rest of the app consumes.
 *
 * The accounts registry (`accounts`) is SHARED across all tabs of the
 * same origin (localStorage). What makes two tabs show two DIFFERENT accounts
 * simultaneously is that "who is active in THIS tab" lives in `sessionStorage`,
 * which is per-tab by definition — NOT in the URL, which must stay portable so
 * a copied link means the same thing on every device (see
 * `infra/MULTIACCOUNT_URL_OPTIONS.md`).
 *
 * The registry's `activeUserId` is demoted to "last-used default": it only
 * seeds a FRESH tab and NEVER overrides a tab's own pin.
 *
 * CRITICAL: resolution is SYNCHRONOUS end to end (`sessionStorage.getItem` +
 * `localStorage.getItem`), because `getAuthToken` / the `X-Active-Account`
 * header resolver, the session machine's module-init reconcile and the
 * PowerSync prime all run before any await is possible.
 */

import {
  type AccountsState,
  getActiveAccount,
  type Session,
  type StoredAccount,
  VISITOR_ID,
} from "@aula/shared";
import { readLastActiveAccountId, readTabAccountId, writeTabAccountId } from "@/lib/tab-account";

/**
 * The account a FRESH tab (no pin yet) should land on:
 *   1. the device's last-activated account, else
 *   2. the registry's `activeUserId` (legacy default), else
 *   3. the first account.
 * `null` only when the registry is empty (the caller mints the guest).
 */
export function defaultAccountId(state: AccountsState): string | null {
  if (state.accounts.length === 0) {
    return null;
  }
  const remembered = readLastActiveAccountId();
  if (remembered !== null && state.accounts.some((a) => a.userId === remembered)) {
    return remembered;
  }
  const active = getActiveAccount(state);
  if (active) {
    return active.userId;
  }
  return state.accounts[0]?.userId ?? null;
}

/**
 * Resolves the account THIS tab should treat as active.
 *
 * Falls back to {@link defaultAccountId} when the tab has no pin yet (a fresh
 * tab) OR when its pinned account is GONE from the registry (it was signed out
 * / removed in another tab). That fallback is what replaces the old positional
 * `tab-account-pin.ts` workaround: an id keeps its meaning when the registry
 * mutates, so there is no index to repair — only the "my account disappeared"
 * case, handled here by degrading to the default instead of showing a phantom.
 */
export function resolveTabAccount(
  state: AccountsState,
  tabAccountId: string | null = readTabAccountId(),
): StoredAccount | null {
  if (state.accounts.length === 0) {
    return null;
  }
  if (tabAccountId !== null) {
    const pinned = state.accounts.find((a) => a.userId === tabAccountId);
    if (pinned) {
      return pinned;
    }
  }
  const fallbackId = defaultAccountId(state);
  return state.accounts.find((a) => a.userId === fallbackId) ?? null;
}

/**
 * Makes this tab's pin EXPLICIT, so every later read is stable and independent
 * of the device-wide default drifting under it (another tab switching accounts
 * updates `LAST_ACTIVE_ACCOUNT_KEY`, which must not silently move THIS tab).
 *
 * Idempotent: only writes when the tab has no pin, or its pin no longer names a
 * registry account. Returns the resolved account id, or `null` when the
 * registry is empty (the caller mints the guest and calls again).
 */
export function ensureTabAccount(state: AccountsState): string | null {
  const current = readTabAccountId();
  if (current !== null && state.accounts.some((a) => a.userId === current)) {
    return current;
  }
  const resolved = defaultAccountId(state);
  if (resolved === null) {
    return null;
  }
  writeTabAccountId(resolved);
  return resolved;
}

/**
 * The per-tab PowerSync DB/sync user id, or `null` when this tab has no
 * resolvable account (an empty registry on a cold boot). A GUEST account
 * resolves to {@link VISITOR_ID}; a REAL account to its own `userId`.
 *
 * SINGLE SOURCE OF TRUTH for "which account does THIS tab sync": the PowerSync
 * provider uses it for BOTH the per-user DB binding AND the connect/auth gate,
 * so the tab that DISPLAYS an account is exactly the tab that SYNCS it —
 * coherent with `useSession().isAuthenticated()`, which derives from the same
 * per-tab account.
 */
export function resolvePerTabSyncUserId(
  state: AccountsState,
  tabAccountId: string | null,
): string | null {
  const account = resolveTabAccount(state, tabAccountId);
  if (account === null) {
    return null;
  }
  return account.kind === "guest" ? VISITOR_ID : account.userId;
}

// ─── Per-tab session VIEW (auth/role/identity gating) ───────────────────────

/**
 * Decodes a JWT's payload claims without verifying the signature. The payload
 * is the middle base64url segment. Returns `null` for anything malformed.
 */
export function decodeJwtClaims(token: string | null | undefined): object | null {
  if (!token) {
    return null;
  }
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) {
    return null;
  }
  try {
    const base64 = parts[1].replaceAll("-", "+").replaceAll("_", "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = globalThis.atob(padded);
    const decoded: unknown = JSON.parse(json);
    return typeof decoded === "object" && decoded !== null ? decoded : null;
  } catch {
    return null;
  }
}

/** Extracts the `role` claim from an account's JWT (UX gating only). */
export function decodeJwtRole(token: string | null | undefined): string | null {
  const claims = decodeJwtClaims(token);
  if (claims === null) {
    return null;
  }
  const role: unknown = Reflect.get(claims, "role");
  return typeof role === "string" ? role : null;
}

/** The per-tab auth/role/identity view derived from the active account. */
export interface ActiveAccountView {
  account: StoredAccount | null;
  isGuest: boolean;
  isAuthenticated: boolean;
  role: string | null;
  user: Session | undefined;
}

/**
 * SINGLE SOURCE OF TRUTH for per-tab auth/role/identity gating.
 */
export function resolveActiveAccountView(
  state: AccountsState,
  tabAccountId: string | null,
  globalSession: Session | null | undefined,
): ActiveAccountView {
  const account = resolveTabAccount(state, tabAccountId);

  if (account === null) {
    const fallback = globalSession ?? undefined;
    return {
      account: null,
      isGuest: !fallback,
      isAuthenticated: Boolean(fallback),
      role: fallback?.user.role ?? null,
      user: fallback,
    };
  }

  if (account.kind === "guest") {
    return { account, isGuest: true, isAuthenticated: false, role: null, user: undefined };
  }

  if (globalSession && globalSession.user.id === account.userId) {
    const role = globalSession.user.role ?? account.role ?? decodeJwtRole(account.token);
    const user =
      role === globalSession.user.role
        ? globalSession
        : { ...globalSession, user: { ...globalSession.user, role } };
    return { account, isGuest: false, isAuthenticated: true, role, user };
  }

  const role = account.role ?? decodeJwtRole(account.token);
  const user: Session = {
    user: {
      id: account.userId,
      email: account.email,
      name: account.displayName,
      image: account.avatar ?? null,
      role,
    },
    session: { id: "", userId: account.userId, expiresAt: "", token: account.token ?? "" },
  };
  return { account, isGuest: false, isAuthenticated: true, role, user };
}
